import React, { useState, useEffect } from 'react';
import useAuth from '../hooks/useAuth';
import {
    Box, Typography, Tabs, Tab, Paper, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Chip, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, MenuItem, Grid, Card, CardContent,
    CardActions, FormControlLabel, Checkbox, Alert, Tooltip, CircularProgress,
    Select, InputLabel, FormControl, TablePagination, InputAdornment, useTheme, 
    useMediaQuery, Divider
} from '@mui/material';
import { StandardTableContainer, StandardTable, StandardTableHead, StandardHeaderCell, StandardBodyCell, useTableColors } from "../components/StandardTable";
import {
    Add, Edit, Delete, Phone, WhatsApp, Email, CheckCircle, Cancel,
    Build, Warning, Info, PriorityHigh, Search, FilterList, Visibility
} from '@mui/icons-material';
import useAxiosPrivate from '../hooks/useAxiosPrivate';
import { brandColors } from '../theme/colors';
import { useTranslation } from 'react-i18next';

// Configurazione colori priorità
const priorityColors = {
    urgente: '#d32f2f',
    alta: '#f57c00',
    media: '#fbc02d',
    bassa: '#388e3c'
};

// Configurazione colori stato (Kanban flow)
const statusColors = {
    richiesta: '#2196f3',
    in_corso: '#ff9800',
    completata: '#4caf50',
    annullata: '#f44336'
};

// Configurazione icone categorie
const categoryIcons = {
    idraulica: '💧',
    elettrica: '⚡',
    riscaldamento: '🔥',
    climatizzazione: '❄️',
    serramenti: '🚪',
    tinteggiatura: '🎨',
    giardino: '🌳',
    attrezzature_mediche: '🏥',
    pulizie: '🧹',
    disinfestazione: '🐛',
    servizi_it: '💻',
    altro: '🔧'
};

// Traduzioni italiano
const translations = {
    priority: {
        urgente: 'Urgente',
        alta: 'Alta',
        media: 'Media',
        bassa: 'Bassa'
    },
    status: {
        richiesta: 'Richiesta',
        in_corso: 'In Corso',
        completata: 'Completata',
        annullata: 'Annullata'
    },
    category: {
        idraulica: 'Idraulica',
        elettrica: 'Elettrica',
        riscaldamento: 'Riscaldamento',
        climatizzazione: 'Climatizzazione',
        serramenti: 'Serramenti',
        tinteggiatura: 'Tinteggiatura',
        giardino: 'Giardino',
        attrezzature_mediche: 'Attrezzature Mediche',
        pulizie: 'Pulizie',
        disinfestazione: 'Disinfestazione',
        servizi_it: 'Servizi IT',
        altro: 'Altro'
    },
    maintenanceType: {
        programmata: 'Programmata',
        straordinaria: 'Straordinaria'
    },
    recurrencePattern: {
        giornaliero: 'Giornaliero',
        settimanale: 'Settimanale',
        mensile: 'Mensile',
        trimestrale: 'Trimestrale',
        semestrale: 'Semestrale',
        annuale: 'Annuale'
    },
    specialization: {
        idraulica: 'Idraulica',
        elettrica: 'Elettrica',
        riscaldamento: 'Riscaldamento',
        climatizzazione: 'Climatizzazione',
        serramenti: 'Serramenti',
        tinteggiatura: 'Tinteggiatura',
        giardino: 'Giardino',
        attrezzature_mediche: 'Attrezzature Mediche',
        pulizie: 'Pulizie',
        disinfestazione: 'Disinfestazione',
        servizi_it: 'Servizi IT',
        altro: 'Altro'
    }
};

const MaintenancePage = () => {
    const { auth } = useAuth();
    const axiosPrivate = useAxiosPrivate();
    const { t } = useTranslation('global');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [currentTab, setCurrentTab] = useState(0);
    const [loading, setLoading] = useState(true);
    
    // Stati dati
    const [requests, setRequests] = useState([]);
    const [providers, setProviders] = useState([]);
    const [filteredRequests, setFilteredRequests] = useState([]);
    
    // Stati filtri
    const [filterStatus, setFilterStatus] = useState('');
    const [filterPriority, setFilterPriority] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [searchText, setSearchText] = useState('');
    
    // Stati paginazione
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    
    // Stati dialog
    const [openRequestDialog, setOpenRequestDialog] = useState(false);
    const [openProviderDialog, setOpenProviderDialog] = useState(false);
    const [openContactDialog, setOpenContactDialog] = useState(false);
    const [openDetailDialog, setOpenDetailDialog] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    
    // Stati form richiesta
    const [requestForm, setRequestForm] = useState({
        maintenance_type: 'straordinaria',
        category: 'idraulica',
        title: '',
        description: '',
        priority: 'media',
        location: '',
        room_number: '',
        reported_by: '',
        scheduled_date: '',
        scheduled_time: '',
        provider_id: '',
        estimated_cost: '',
        is_recurring: false,
        recurrence_pattern: '',
        recurrence_interval: 1,
        recurrence_end_date: ''
    });
    
    // Stati form manutentore
    const [providerForm, setProviderForm] = useState({
        company_name: '',
        contact_person: '',
        email: '',
        phone: '',
        whatsapp_number: '',
        specialization: 'idraulica',
        vat_number: '',
        address: '',
        city: '',
        postal_code: '',
        has_contract: false,
        contract_notes: '',
        notes: ''
    });
    
    // Stati contatto
    const [contactMethod, setContactMethod] = useState('whatsapp');
    const [contactInfo, setContactInfo] = useState(null);

    // Carica dati iniziali
    useEffect(() => {
        if (!auth?.accessToken) {
            return;
        }
        loadData();
    }, [auth?.accessToken]);

    // Applica filtri
    useEffect(() => {
        applyFilters();
    }, [requests, filterStatus, filterPriority, filterCategory, searchText, currentTab]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [requestsRes, providersRes] = await Promise.all([
                axiosPrivate.get('/maintenance/requests'),
                axiosPrivate.get('/maintenance/providers')
            ]);
            setRequests(requestsRes.data);
            setProviders(providersRes.data);
        } catch (error) {
            console.error('Errore nel caricamento dei dati:', error);
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = requests;

        // Filtra per tipo (tab corrente)
        if (currentTab === 0) {
            filtered = filtered.filter(r => r.maintenance_type === 'straordinaria');
        } else if (currentTab === 1) {
            filtered = filtered.filter(r => r.maintenance_type === 'programmata');
        }

        // Filtra per status
        if (filterStatus) {
            filtered = filtered.filter(r => r.status === filterStatus);
        }

        // Filtra per priorità
        if (filterPriority) {
            filtered = filtered.filter(r => r.priority === filterPriority);
        }

        // Filtra per categoria
        if (filterCategory) {
            filtered = filtered.filter(r => r.category === filterCategory);
        }

        // Filtra per testo di ricerca
        if (searchText) {
            const search = searchText.toLowerCase();
            filtered = filtered.filter(r =>
                r.title?.toLowerCase().includes(search) ||
                r.description?.toLowerCase().includes(search) ||
                r.location?.toLowerCase().includes(search) ||
                r.company_name?.toLowerCase().includes(search)
            );
        }

        setFilteredRequests(filtered);
        setPage(0); // Reset paginazione
    };

    // Gestione tab
    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
        resetFilters();
    };

    const resetFilters = () => {
        setFilterStatus('');
        setFilterPriority('');
        setFilterCategory('');
        setSearchText('');
    };

    // ========================================
    // GESTIONE RICHIESTE
    // ========================================

    const handleOpenRequestDialog = (request = null) => {
        if (request) {
            setIsEditMode(true);
            setSelectedRequest(request);
            setRequestForm({
                maintenance_type: request.maintenance_type,
                category: request.category,
                title: request.title,
                description: request.description || '',
                priority: request.priority,
                location: request.location || '',
                room_number: request.room_number || '',
                reported_by: request.reported_by || '',
                scheduled_date: request.scheduled_date ? request.scheduled_date.split('T')[0] : '',
                scheduled_time: request.scheduled_time || '',
                provider_id: request.provider_id || '',
                estimated_cost: request.estimated_cost || '',
                is_recurring: request.is_recurring || false,
                recurrence_pattern: request.recurrence_pattern || '',
                recurrence_interval: request.recurrence_interval || 1,
                recurrence_end_date: request.recurrence_end_date ? request.recurrence_end_date.split('T')[0] : ''
            });
        } else {
            setIsEditMode(false);
            setSelectedRequest(null);
            setRequestForm({
                maintenance_type: currentTab === 0 ? 'straordinaria' : 'programmata',
                category: 'idraulica',
                title: '',
                description: '',
                priority: 'media',
                location: '',
                room_number: '',
                reported_by: '',
                scheduled_date: '',
                scheduled_time: '',
                provider_id: '',
                estimated_cost: '',
                is_recurring: false,
                recurrence_pattern: '',
                recurrence_interval: 1,
                recurrence_end_date: ''
            });
        }
        setOpenRequestDialog(true);
    };

    const handleSaveRequest = async () => {
        try {
            // Normalize payload to avoid sending empty strings that may break SQL/FK checks
            const payload = {
                ...requestForm,
            };

            // sanitize nullable fields (convert empty strings to null)
            payload.provider_id = payload.provider_id || null;
            payload.scheduled_date = payload.scheduled_date || null;
            payload.scheduled_time = payload.scheduled_time || null;
            payload.estimated_cost = payload.estimated_cost !== '' ? payload.estimated_cost : null;
            payload.actual_cost = payload.actual_cost !== '' ? payload.actual_cost : null;
            payload.recurrence_end_date = payload.recurrence_end_date || null;
            payload.recurrence_pattern = payload.recurrence_pattern || null;
            payload.recurrence_interval = payload.recurrence_interval || null;
            payload.reported_by = payload.reported_by || null;
            payload.location = payload.location || null;
            payload.room_number = payload.room_number || null;
            payload.description = payload.description || null;

            if (isEditMode) {
                // preserve status from selectedRequest unless form explicitly changed it
                payload.status = selectedRequest.status;
                await axiosPrivate.put(`/maintenance/requests/${selectedRequest.request_id}`, payload);
            } else {
                await axiosPrivate.post('/maintenance/requests', payload);
            }

            setOpenRequestDialog(false);
            loadData();
        } catch (error) {
            console.error('Errore nel salvataggio della richiesta:', error);
            alert(error.response?.data?.message || 'Errore nel salvataggio della richiesta');
        }
    };

    const handleDeleteRequest = async (requestId) => {
        if (!window.confirm('Sei sicuro di voler annullare questa richiesta?')) return;
        
        try {
            await axiosPrivate.delete(`/maintenance/requests/${requestId}`);
            loadData();
        } catch (error) {
            console.error('Errore nell\'annullamento della richiesta:', error);
            alert('Errore nell\'annullamento della richiesta');
        }
    };

    const handleCompleteRequest = async (requestId) => {
        const actualCost = window.prompt('Inserisci il costo effettivo (opzionale):');
        const completionNotes = window.prompt('Note di completamento (opzionale):');
        
        try {
            await axiosPrivate.post(`/maintenance/requests/${requestId}/complete`, {
                actual_cost: actualCost || null,
                completion_notes: completionNotes || null
            });
            loadData();
        } catch (error) {
            console.error('Errore nel completamento:', error);
            alert('Errore nel completamento dell\'intervento');
        }
    };

    const handleStatusChange = async (requestId, newStatus) => {
        try {
            // Se cambia a 'completata', chiedi costo e note
            if (newStatus === 'completata') {
                const actualCost = window.prompt('Inserisci il costo effettivo (opzionale):');
                const completionNotes = window.prompt('Note di completamento (opzionale):');
                
                await axiosPrivate.post(`/maintenance/requests/${requestId}/complete`, {
                    actual_cost: actualCost || null,
                    completion_notes: completionNotes || null
                });
            } else {
                // Aggiorna solo lo status - prendi la richiesta corrente
                const request = requests.find(r => r.request_id === requestId);
                
                // Crea payload pulito con solo i campi necessari
                const payload = {
                    maintenance_type: request.maintenance_type,
                    category: request.category,
                    title: request.title,
                    description: request.description || null,
                    priority: request.priority,
                    location: request.location || null,
                    room_number: request.room_number || null,
                    reported_by: request.reported_by || null,
                    scheduled_date: request.scheduled_date || null,
                    scheduled_time: request.scheduled_time || null,
                    provider_id: request.provider_id || null,
                    estimated_cost: request.estimated_cost || null,
                    actual_cost: request.actual_cost || null,
                    is_recurring: request.is_recurring || false,
                    recurrence_pattern: request.recurrence_pattern || null,
                    recurrence_interval: request.recurrence_interval || null,
                    recurrence_end_date: request.recurrence_end_date || null,
                    status: newStatus
                };
                
                await axiosPrivate.put(`/maintenance/requests/${requestId}`, payload);
            }
            loadData();
        } catch (error) {
            console.error('Errore nel cambio status:', error);
            alert('Errore nel cambio status: ' + (error.response?.data?.message || error.message));
        }
    };

    // ========================================
    // GESTIONE CONTATTI
    // ========================================

    const handleOpenContactDialog = async (request) => {
        setSelectedRequest(request);
        setContactMethod(request.whatsapp_number ? 'whatsapp' : 'email');
        setOpenContactDialog(true);
    };

    const handleSendContact = async () => {
        try {
            const response = await axiosPrivate.post(
                `/maintenance/requests/${selectedRequest.request_id}/contact`,
                { contact_method: contactMethod }
            );
            
            setContactInfo(response.data.contactInfo);
            
            // Apri automaticamente WhatsApp o client email
            if (response.data.contactInfo.type === 'whatsapp') {
                window.open(response.data.contactInfo.url, '_blank');
            } else if (response.data.contactInfo.type === 'email') {
                const { email, subject, message } = response.data.contactInfo;
                window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
            }
            
            setTimeout(() => {
                setOpenContactDialog(false);
                setContactInfo(null);
                loadData();
            }, 2000);
        } catch (error) {
            console.error('Errore nel contatto:', error);
            alert(error.response?.data?.message || 'Errore nel contatto del manutentore');
        }
    };

    // ========================================
    // GESTIONE MANUTENTORI
    // ========================================

    const handleOpenProviderDialog = (provider = null) => {
        if (provider) {
            setIsEditMode(true);
            setSelectedProvider(provider);
            setProviderForm({...provider});
        } else {
            setIsEditMode(false);
            setSelectedProvider(null);
            setProviderForm({
                company_name: '',
                contact_person: '',
                email: '',
                phone: '',
                whatsapp_number: '',
                specialization: 'idraulica',
                vat_number: '',
                address: '',
                city: '',
                postal_code: '',
                has_contract: false,
                contract_notes: '',
                notes: ''
            });
        }
        setOpenProviderDialog(true);
    };

    const handleSaveProvider = async () => {
        try {
            if (isEditMode) {
                await axiosPrivate.put(`/maintenance/providers/${selectedProvider.provider_id}`, {
                    ...providerForm,
                    is_active: true
                });
            } else {
                await axiosPrivate.post('/maintenance/providers', providerForm);
            }
            setOpenProviderDialog(false);
            loadData();
        } catch (error) {
            console.error('Errore nel salvataggio del manutentore:', error);
            alert('Errore nel salvataggio del manutentore');
        }
    };

    const handleDeleteProvider = async (providerId) => {
        if (!window.confirm('Sei sicuro di voler disattivare questo manutentore?')) return;
        
        try {
            await axiosPrivate.delete(`/maintenance/providers/${providerId}`);
            loadData();
        } catch (error) {
            console.error('Errore nella disattivazione:', error);
            alert('Errore nella disattivazione del manutentore');
        }
    };

    // ========================================
    // DETTAGLIO RICHIESTA
    // ========================================

    const handleOpenDetailDialog = async (request) => {
        try {
            const response = await axiosPrivate.get(`/maintenance/requests/${request.request_id}`);
            setSelectedRequest(response.data);
            setOpenDetailDialog(true);
        } catch (error) {
            console.error('Errore nel caricamento del dettaglio:', error);
        }
    };

    // ========================================
    // RENDER COMPONENTI
    // ========================================

    const renderRequestsTable = () => {
        if (isMobile) {
            // Vista mobile con cards
            return (
                <Box sx={{ mt: 2 }}>
                    {filteredRequests.length === 0 ? (
                        <Paper sx={{ p: 3, textAlign: 'center' }}>
                            <Typography color="textSecondary">Nessuna richiesta trovata</Typography>
                        </Paper>
                    ) : (
                        filteredRequests
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((request) => (
                                <MaintenanceCardMobile key={request.maintenance_id} request={request} />
                            ))
                    )}
                    <TablePagination
                        component="div"
                        count={filteredRequests.length}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        labelRowsPerPage="Righe:"
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
                    />
                </Box>
            );
        }
        
        // Vista desktop con tabella
        return (
        <StandardTableContainer sx={{ mt: 2 }}>
            <StandardTable>
                <StandardTableHead>
                    <TableRow>
                        <StandardHeaderCell>{t('maintenance.table.priority', 'Priorità')}</StandardHeaderCell>
                        <StandardHeaderCell>{t('maintenance.table.category', 'Categoria')}</StandardHeaderCell>
                        <StandardHeaderCell>{t('maintenance.table.title', 'Titolo')}</StandardHeaderCell>
                        <StandardHeaderCell>{t('maintenance.table.location', 'Luogo')}</StandardHeaderCell>
                        <StandardHeaderCell>{t('maintenance.table.expectedDate', 'Data Prevista')}</StandardHeaderCell>
                        <StandardHeaderCell>{t('maintenance.table.status', 'Stato')}</StandardHeaderCell>
                        <StandardHeaderCell align="center">{t('maintenance.table.actions', 'Azioni')}</StandardHeaderCell>
                    </TableRow>
                </StandardTableHead>
                <TableBody>
                    {filteredRequests.length === 0 ? (
                        <TableRow>
                            <StandardBodyCell colSpan={7} align="center">
                                <Typography color="textSecondary">{t('maintenance.noRequests', 'Nessuna richiesta trovata')}</Typography>
                            </StandardBodyCell>
                        </TableRow>
                    ) : (
                        filteredRequests
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((request, index) => (
                                <TableRow key={request.request_id} hover>
                                    <StandardBodyCell>
                                        <Chip
                                            label={translations.priority[request.priority]}
                                            size="small"
                                            sx={{
                                                backgroundColor: priorityColors[request.priority],
                                                color: '#fff',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                    </StandardBodyCell>
                                    <StandardBodyCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <span>{categoryIcons[request.category]}</span>
                                            {translations.category[request.category]}
                                        </Box>
                                    </StandardBodyCell>
                                    <StandardBodyCell>
                                        <Typography variant="body2" fontWeight="bold">
                                            {request.title}
                                        </Typography>
                                        {request.description && (
                                            <Typography variant="caption" color="textSecondary">
                                                {request.description.substring(0, 50)}...
                                            </Typography>
                                        )}
                                    </StandardBodyCell>
                                    <StandardBodyCell>
                                        {request.location}
                                        {request.room_number && (
                                            <Typography variant="caption" display="block">
                                                Stanza {request.room_number}
                                            </Typography>
                                        )}
                                    </StandardBodyCell>
                                    <StandardBodyCell>
                                        {request.scheduled_date ? (
                                            <>
                                                {new Date(request.scheduled_date).toLocaleDateString('it-IT')}
                                                {request.scheduled_time && (
                                                    <Typography variant="caption" display="block">
                                                        {request.scheduled_time.substring(0, 5)}
                                                    </Typography>
                                                )}
                                            </>
                                        ) : (
                                            <Typography variant="caption" color="textSecondary">
                                                Da programmare
                                            </Typography>
                                        )}
                                    </StandardBodyCell>
                                    <StandardBodyCell>
                                        {request.status === 'completata' || request.status === 'annullata' ? (
                                            <Chip
                                                label={translations.status[request.status]}
                                                size="small"
                                                sx={{
                                                    backgroundColor: statusColors[request.status],
                                                    color: '#fff',
                                                    fontWeight: 'bold'
                                                }}
                                            />
                                        ) : (
                                            <FormControl size="small" fullWidth>
                                                <Select
                                                    value={request.status}
                                                    onChange={(e) => handleStatusChange(request.request_id, e.target.value)}
                                                    sx={{
                                                        minWidth: 130,
                                                        height: 32,
                                                        backgroundColor: statusColors[request.status],
                                                        color: 'white',
                                                        fontWeight: 'bold',
                                                        borderRadius: '16px',
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            border: 'none'
                                                        },
                                                        '& .MuiSelect-select': {
                                                            paddingTop: '6px',
                                                            paddingBottom: '6px',
                                                            paddingLeft: '12px',
                                                            paddingRight: '32px'
                                                        },
                                                        '& .MuiSelect-icon': {
                                                            color: 'white'
                                                        },
                                                        '&:hover': {
                                                            backgroundColor: statusColors[request.status],
                                                            opacity: 0.9
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value="richiesta">
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Box sx={{ 
                                                                width: 12, 
                                                                height: 12, 
                                                                borderRadius: '50%', 
                                                                backgroundColor: statusColors.richiesta 
                                                            }} />
                                                            Richiesta
                                                        </Box>
                                                    </MenuItem>
                                                    <MenuItem value="in_corso">
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Box sx={{ 
                                                                width: 12, 
                                                                height: 12, 
                                                                borderRadius: '50%', 
                                                                backgroundColor: statusColors.in_corso 
                                                            }} />
                                                            In Corso
                                                        </Box>
                                                    </MenuItem>
                                                    <MenuItem value="completata">
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Box sx={{ 
                                                                width: 12, 
                                                                height: 12, 
                                                                borderRadius: '50%', 
                                                                backgroundColor: statusColors.completata 
                                                            }} />
                                                            Completata
                                                        </Box>
                                                    </MenuItem>
                                                    <MenuItem value="annullata">
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Box sx={{ 
                                                                width: 12, 
                                                                height: 12, 
                                                                borderRadius: '50%', 
                                                                backgroundColor: statusColors.annullata 
                                                            }} />
                                                            Annullata
                                                        </Box>
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                        )}
                                    </StandardBodyCell>
                                    <StandardBodyCell align="center">
                                        <Tooltip title={t('maintenance.actions.details', 'Dettagli')}>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => handleOpenDetailDialog(request)}
                                            >
                                                <Visibility />
                                            </IconButton>
                                        </Tooltip>
                                        {request.status !== 'completata' && request.status !== 'annullata' && (
                                            <>
                                                <Tooltip title={t('common.edit', 'Modifica')}>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleOpenRequestDialog(request)}
                                                    >
                                                        <Edit />
                                                    </IconButton>
                                                </Tooltip>
                                                {request.provider_id && (
                                                    <Tooltip title={t('maintenance.actions.contact', 'Contatta')}>
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleOpenContactDialog(request)}
                                                        >
                                                            <WhatsApp />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {request.status === 'in_corso' && (
                                                    <Tooltip title={t('maintenance.actions.complete', 'Completa')}>
                                                        <IconButton
                                                            size="small"
                                                            color="success"
                                                            onClick={() => handleCompleteRequest(request.request_id)}
                                                        >
                                                            <CheckCircle />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title={t('maintenance.actions.cancel', 'Annulla')}>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteRequest(request.request_id)}
                                                    >
                                                        <Cancel />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        )}
                                    </StandardBodyCell>
                                </TableRow>
                            ))
                    )}
                </TableBody>
            </StandardTable>
            <TablePagination
                component="div"
                count={filteredRequests.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                }}
                labelRowsPerPage={t('common.rowsPerPage', 'Righe per pagina:')}
                labelDisplayedRows={({ from, to, count }) => t('common.displayedRows', { from, to, count, defaultValue: `${from}-${to} di ${count}` })}
            />
        </StandardTableContainer>
        );
    };

    const renderProvidersGrid = () => (
        <Grid container spacing={3} sx={{ mt: 2 }}>
            {providers.length === 0 ? (
                <Grid item xs={12}>
                    <Paper sx={{ p: 3, textAlign: 'center' }}>
                        <Typography color="textSecondary">{t('maintenance.noProviders', 'Nessun manutentore registrato')}</Typography>
                    </Paper>
                </Grid>
            ) : (
                providers.map((provider) => (
                    <Grid item xs={12} sm={6} md={4} key={provider.provider_id}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                    <Typography variant="h6" component="div">
                                        {provider.company_name}
                                    </Typography>
                                    <Chip
                                        label={translations.specialization[provider.specialization]}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                    />
                                </Box>
                                
                                {provider.contact_person && (
                                    <Typography variant="body2" color="textSecondary" gutterBottom>
                                        <strong>Contatto:</strong> {provider.contact_person}
                                    </Typography>
                                )}
                                
                                {provider.phone && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Phone fontSize="small" color="action" />
                                        <Typography variant="body2">{provider.phone}</Typography>
                                    </Box>
                                )}
                                
                                {provider.whatsapp_number && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <WhatsApp fontSize="small" sx={{ color: brandColors.whatsapp.primary }} />
                                        <Typography variant="body2">{provider.whatsapp_number}</Typography>
                                    </Box>
                                )}
                                
                                {provider.email && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Email fontSize="small" color="action" />
                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                            {provider.email}
                                        </Typography>
                                    </Box>
                                )}
                                
                                {provider.has_contract && (
                                    <Chip
                                        label="Contratto Attivo"
                                        size="small"
                                        color="success"
                                        sx={{ mt: 1 }}
                                    />
                                )}
                                
                                {provider.contract_notes && (
                                    <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
                                        {provider.contract_notes}
                                    </Typography>
                                )}
                            </CardContent>
                            <CardActions>
                                <Button
                                    size="small"
                                    startIcon={<Edit />}
                                    onClick={() => handleOpenProviderDialog(provider)}
                                >
                                    Modifica
                                </Button>
                                <Button
                                    size="small"
                                    color="error"
                                    startIcon={<Delete />}
                                    onClick={() => handleDeleteProvider(provider.provider_id)}
                                >
                                    Disattiva
                                </Button>
                            </CardActions>
                        </Card>
                    </Grid>
                ))
            )}
        </Grid>
    );

    // Componente card mobile per richieste manutenzione
    const MaintenanceCardMobile = ({ request }) => (
        <Card sx={{ mb: 2, border: `2px solid ${priorityColors[request.priority]}` }}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                        {categoryIcons[request.category]} {request.title}
                    </Typography>
                    <Chip
                        label={translations.priority[request.priority]}
                        size="small"
                        sx={{
                            backgroundColor: priorityColors[request.priority],
                            color: 'white',
                            fontWeight: 600
                        }}
                    />
                </Box>
                
                <Chip
                    label={translations.status[request.status]}
                    size="small"
                    sx={{
                        backgroundColor: statusColors[request.status],
                        color: 'white',
                        mb: 1
                    }}
                />
                
                <Divider sx={{ my: 1 }} />
                
                <Grid container spacing={1} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">Tipo</Typography>
                        <Typography variant="body2">{translations.maintenanceType[request.maintenance_type]}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="textSecondary">Categoria</Typography>
                        <Typography variant="body2">{translations.category[request.category]}</Typography>
                    </Grid>
                    {request.location && (
                        <Grid item xs={12}>
                            <Typography variant="caption" color="textSecondary">Luogo</Typography>
                            <Typography variant="body2">{request.location} {request.room_number && `- Stanza ${request.room_number}`}</Typography>
                        </Grid>
                    )}
                    {request.scheduled_date && (
                        <Grid item xs={12}>
                            <Typography variant="caption" color="textSecondary">Data Programmata</Typography>
                            <Typography variant="body2">
                                {new Date(request.scheduled_date).toLocaleDateString('it-IT')}
                                {request.scheduled_time && ` - ${request.scheduled_time}`}
                            </Typography>
                        </Grid>
                    )}
                    {request.provider_name && (
                        <Grid item xs={12}>
                            <Typography variant="caption" color="textSecondary">Manutentore</Typography>
                            <Typography variant="body2">{request.provider_name}</Typography>
                        </Grid>
                    )}
                </Grid>
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end', gap: 1, px: 2, pb: 2 }}>
                <IconButton
                    size="small"
                    color="primary"
                    onClick={() => { setSelectedRequest(request); setOpenDetailDialog(true); }}
                >
                    <Visibility />
                </IconButton>
                <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleOpenRequestDialog(request)}
                >
                    <Edit />
                </IconButton>
                <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteRequest(request.maintenance_id)}
                >
                    <Delete />
                </IconButton>
            </CardActions>
        </Card>
    );

    // Continua nella prossima parte...
    return (
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, minHeight: '100vh', bgcolor: 'background.default' }}>
            <Card sx={{ p: isMobile ? 2 : 3, mb: 2 }}>
                <Typography variant={isMobile ? "h5" : "h4"} component="h1" fontWeight={600}>
                    {t('maintenance.title', 'Gestione Manutenzioni')}
                </Typography>
            </Card>

            <Paper sx={{ width: '100%', mb: 2 }}>
                <Tabs value={currentTab} onChange={handleTabChange}>
                    <Tab label={t('maintenance.tabs.extraordinary', 'Manutenzioni Straordinarie')} />
                    <Tab label={t('maintenance.tabs.scheduled', 'Manutenzioni Programmate')} />
                    <Tab label={t('maintenance.tabs.providers', 'Manutentori')} />
                </Tabs>
            </Paper>

            {/* Barra filtri e azioni per tab 0 e 1 */}
            {currentTab < 2 && (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder={t('maintenance.search', 'Cerca...')}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>{t('maintenance.filters.status', 'Stato')}</InputLabel>
                                <Select
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                    label={t('maintenance.filters.status', 'Stato')}
                                >
                                    <MenuItem value="">{t('maintenance.filters.all', 'Tutti')}</MenuItem>
                                    <MenuItem value="da_assegnare">{t('maintenance.filters.toAssign', 'Da Assegnare')}</MenuItem>
                                    <MenuItem value="assegnata">{t('maintenance.status.assigned', 'Assegnata')}</MenuItem>
                                    <MenuItem value="in_corso">{t('maintenance.status.in_progress', 'In Corso')}</MenuItem>
                                    <MenuItem value="completata">{t('maintenance.status.completed', 'Completata')}</MenuItem>
                                    <MenuItem value="annullata">{t('maintenance.status.cancelled', 'Annullata')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>{t('maintenance.filters.priority', 'Priorità')}</InputLabel>
                                <Select
                                    value={filterPriority}
                                    onChange={(e) => setFilterPriority(e.target.value)}
                                    label={t('maintenance.filters.priority', 'Priorità')}
                                >
                                    <MenuItem value="">{t('maintenance.filters.allPriorities', 'Tutte')}</MenuItem>
                                    <MenuItem value="urgente">{t('maintenance.priority.urgent', 'Urgente')}</MenuItem>
                                    <MenuItem value="alta">{t('maintenance.priority.high', 'Alta')}</MenuItem>
                                    <MenuItem value="media">{t('maintenance.priority.medium', 'Media')}</MenuItem>
                                    <MenuItem value="bassa">{t('maintenance.priority.low', 'Bassa')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>{t('maintenance.filters.category', 'Categoria')}</InputLabel>
                                <Select
                                    value={filterCategory}
                                    onChange={(e) => setFilterCategory(e.target.value)}
                                    label={t('maintenance.filters.category', 'Categoria')}
                                >
                                    <MenuItem value="">{t('maintenance.filters.allCategories', 'Tutte')}</MenuItem>
                                    {Object.keys(translations.category).map(key => (
                                        <MenuItem key={key} value={key}>
                                            {categoryIcons[key]} {translations.category[key]}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <Button
                                fullWidth
                               
                                startIcon={<FilterList />}
                                onClick={resetFilters}
                            >
                                {t('maintenance.filters.reset', 'Reset Filtri')}
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6} md={1}>
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => handleOpenRequestDialog()}
                            >
                                Nuovo
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* Pulsante nuovo manutentore per tab 2 */}
            {currentTab === 2 && (
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => handleOpenProviderDialog()}
                    >
                        Nuovo Manutentore
                    </Button>
                </Box>
            )}

            {/* Contenuto tab */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {currentTab < 2 && renderRequestsTable()}
                    {currentTab === 2 && renderProvidersGrid()}
                </>
            )}

            {/* ======================================== */}
            {/* DIALOG RICHIESTA MANUTENZIONE */}
            {/* ======================================== */}
            <Dialog
                open={openRequestDialog}
                onClose={() => setOpenRequestDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {isEditMode ? 'Modifica Richiesta' : 'Nuova Richiesta di Manutenzione'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Tipo Manutenzione</InputLabel>
                                <Select
                                    value={requestForm.maintenance_type}
                                    onChange={(e) => setRequestForm({...requestForm, maintenance_type: e.target.value})}
                                    label="Tipo Manutenzione"
                                >
                                    <MenuItem value="straordinaria">Straordinaria</MenuItem>
                                    <MenuItem value="programmata">Programmata</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Categoria</InputLabel>
                                <Select
                                    value={requestForm.category}
                                    onChange={(e) => setRequestForm({...requestForm, category: e.target.value})}
                                    label="Categoria"
                                >
                                    {Object.keys(translations.category).map(key => (
                                        <MenuItem key={key} value={key}>
                                            {categoryIcons[key]} {translations.category[key]}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                required
                                label="Titolo"
                                value={requestForm.title}
                                onChange={(e) => setRequestForm({...requestForm, title: e.target.value})}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Descrizione"
                                value={requestForm.description}
                                onChange={(e) => setRequestForm({...requestForm, description: e.target.value})}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Priorità</InputLabel>
                                <Select
                                    value={requestForm.priority}
                                    onChange={(e) => setRequestForm({...requestForm, priority: e.target.value})}
                                    label="Priorità"
                                >
                                    <MenuItem value="bassa">Bassa</MenuItem>
                                    <MenuItem value="media">Media</MenuItem>
                                    <MenuItem value="alta">Alta</MenuItem>
                                    <MenuItem value="urgente">Urgente</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Segnalato da"
                                value={requestForm.reported_by}
                                onChange={(e) => setRequestForm({...requestForm, reported_by: e.target.value})}
                            />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <TextField
                                fullWidth
                                label="Luogo"
                                value={requestForm.location}
                                onChange={(e) => setRequestForm({...requestForm, location: e.target.value})}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="N. Stanza"
                                value={requestForm.room_number}
                                onChange={(e) => setRequestForm({...requestForm, room_number: e.target.value})}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Data Prevista"
                                value={requestForm.scheduled_date}
                                onChange={(e) => setRequestForm({...requestForm, scheduled_date: e.target.value})}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="time"
                                label="Ora Prevista"
                                value={requestForm.scheduled_time}
                                onChange={(e) => setRequestForm({...requestForm, scheduled_time: e.target.value})}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Manutentore</InputLabel>
                                <Select
                                    value={requestForm.provider_id}
                                    onChange={(e) => setRequestForm({...requestForm, provider_id: e.target.value})}
                                    label="Manutentore"
                                >
                                    <MenuItem value="">Nessuno</MenuItem>
                                    {providers
                                        .filter(p => p.specialization === requestForm.category)
                                        .map(p => (
                                            <MenuItem key={p.provider_id} value={p.provider_id}>
                                                {p.company_name}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Preventivo (€)"
                                value={requestForm.estimated_cost}
                                onChange={(e) => setRequestForm({...requestForm, estimated_cost: e.target.value})}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                                }}
                            />
                        </Grid>
                        
                        {/* Ricorrenza solo per manutenzione programmata */}
                        {requestForm.maintenance_type === 'programmata' && (
                            <>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={requestForm.is_recurring}
                                                onChange={(e) => setRequestForm({...requestForm, is_recurring: e.target.checked})}
                                            />
                                        }
                                        label="Manutenzione ricorrente"
                                    />
                                </Grid>
                                {requestForm.is_recurring && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Frequenza</InputLabel>
                                                <Select
                                                    value={requestForm.recurrence_pattern}
                                                    onChange={(e) => setRequestForm({...requestForm, recurrence_pattern: e.target.value})}
                                                    label="Frequenza"
                                                >
                                                    {Object.keys(translations.recurrencePattern).map(key => (
                                                        <MenuItem key={key} value={key}>
                                                            {translations.recurrencePattern[key]}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                            <TextField
                                                fullWidth
                                                type="number"
                                                label="Ogni"
                                                value={requestForm.recurrence_interval}
                                                onChange={(e) => setRequestForm({...requestForm, recurrence_interval: e.target.value})}
                                                InputProps={{ inputProps: { min: 1 } }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                            <TextField
                                                fullWidth
                                                type="date"
                                                label="Fino al"
                                                value={requestForm.recurrence_end_date}
                                                onChange={(e) => setRequestForm({...requestForm, recurrence_end_date: e.target.value})}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Grid>
                                    </>
                                )}
                            </>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" color="primary" onClick={() => setOpenRequestDialog(false)}>{t('common.cancel', 'Annulla')}</Button>
                    <Button
                        onClick={handleSaveRequest}
                        variant="contained"
                        color="primary"
                        disabled={!requestForm.title}
                    >
                        {t('common.save', 'Salva')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ======================================== */}
            {/* DIALOG MANUTENTORE */}
            {/* ======================================== */}
            <Dialog
                open={openProviderDialog}
                onClose={() => setOpenProviderDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {isEditMode ? 'Modifica Manutentore' : 'Nuovo Manutentore'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                required
                                label="Ragione Sociale"
                                value={providerForm.company_name}
                                onChange={(e) => setProviderForm({...providerForm, company_name: e.target.value})}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Persona di Riferimento"
                                value={providerForm.contact_person}
                                onChange={(e) => setProviderForm({...providerForm, contact_person: e.target.value})}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required>
                                <InputLabel>Specializzazione</InputLabel>
                                <Select
                                    value={providerForm.specialization}
                                    onChange={(e) => setProviderForm({...providerForm, specialization: e.target.value})}
                                    label="Specializzazione"
                                >
                                    {Object.keys(translations.specialization).map(key => (
                                        <MenuItem key={key} value={key}>
                                            {categoryIcons[key]} {translations.specialization[key]}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="P.IVA"
                                value={providerForm.vat_number}
                                onChange={(e) => setProviderForm({...providerForm, vat_number: e.target.value})}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Telefono"
                                value={providerForm.phone}
                                onChange={(e) => setProviderForm({...providerForm, phone: e.target.value})}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Phone fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="WhatsApp"
                                value={providerForm.whatsapp_number}
                                onChange={(e) => setProviderForm({...providerForm, whatsapp_number: e.target.value})}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <WhatsApp fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Email"
                                type="email"
                                value={providerForm.email}
                                onChange={(e) => setProviderForm({...providerForm, email: e.target.value})}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Email fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <TextField
                                fullWidth
                                label="Indirizzo"
                                value={providerForm.address}
                                onChange={(e) => setProviderForm({...providerForm, address: e.target.value})}
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <TextField
                                fullWidth
                                label="CAP"
                                value={providerForm.postal_code}
                                onChange={(e) => setProviderForm({...providerForm, postal_code: e.target.value})}
                            />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                            <TextField
                                fullWidth
                                label="Città"
                                value={providerForm.city}
                                onChange={(e) => setProviderForm({...providerForm, city: e.target.value})}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={providerForm.has_contract}
                                        onChange={(e) => setProviderForm({...providerForm, has_contract: e.target.checked})}
                                    />
                                }
                                label="Ha un contratto attivo"
                            />
                        </Grid>
                        {providerForm.has_contract && (
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={2}
                                    label="Note sul Contratto (scadenza, servizi inclusi, etc.)"
                                    value={providerForm.contract_notes}
                                    onChange={(e) => setProviderForm({...providerForm, contract_notes: e.target.value})}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label="Note"
                                value={providerForm.notes}
                                onChange={(e) => setProviderForm({...providerForm, notes: e.target.value})}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" color="primary" onClick={() => setOpenProviderDialog(false)}>{t('common.cancel', 'Annulla')}</Button>
                    <Button
                        onClick={handleSaveProvider}
                        variant="contained"
                        color="primary"
                        disabled={!providerForm.company_name || !providerForm.specialization}
                    >
                        {t('common.save', 'Salva')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ======================================== */}
            {/* DIALOG CONTATTO MANUTENTORE */}
            {/* ======================================== */}
            <Dialog
                open={openContactDialog}
                onClose={() => setOpenContactDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Contatta Manutentore</DialogTitle>
                <DialogContent>
                    {selectedRequest && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Richiesta: {selectedRequest.title}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                Manutentore: {selectedRequest.company_name}
                            </Typography>
                            
                            <FormControl fullWidth sx={{ mt: 2 }}>
                                <InputLabel>Metodo di Contatto</InputLabel>
                                <Select
                                    value={contactMethod}
                                    onChange={(e) => setContactMethod(e.target.value)}
                                    label="Metodo di Contatto"
                                >
                                    {selectedRequest.whatsapp_number && (
                                        <MenuItem value="whatsapp">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <WhatsApp /> WhatsApp ({selectedRequest.whatsapp_number})
                                            </Box>
                                        </MenuItem>
                                    )}
                                    {selectedRequest.email && (
                                        <MenuItem value="email">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Email /> Email ({selectedRequest.email})
                                            </Box>
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormControl>

                            {contactInfo && (
                                <Alert severity="success" sx={{ mt: 2 }}>
                                    Messaggio inviato con successo! 
                                    {contactInfo.type === 'whatsapp' && ' WhatsApp si aprirà in una nuova finestra.'}
                                    {contactInfo.type === 'email' && ' Il client email si aprirà automaticamente.'}
                                </Alert>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" color="primary" onClick={() => setOpenContactDialog(false)}>{t('common.close', 'Chiudi')}</Button>
                    <Button
                        onClick={handleSendContact}
                        variant="contained"
                        color="primary"
                        startIcon={contactMethod === 'whatsapp' ? <WhatsApp /> : <Email />}
                        disabled={contactInfo !== null}
                    >
                        Invia Messaggio
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ======================================== */}
            {/* DIALOG DETTAGLIO RICHIESTA CON STORICO */}
            {/* ======================================== */}
            <Dialog
                open={openDetailDialog}
                onClose={() => setOpenDetailDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Dettaglio Richiesta Manutenzione</DialogTitle>
                <DialogContent>
                    {selectedRequest && (
                        <Box sx={{ mt: 1 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                                        <Chip
                                            label={translations.maintenanceType[selectedRequest.maintenance_type]}
                                            color="primary"
                                        />
                                        <Chip
                                            label={translations.priority[selectedRequest.priority]}
                                            sx={{
                                                backgroundColor: priorityColors[selectedRequest.priority],
                                                color: 'white'
                                            }}
                                        />
                                        <Chip
                                            label={translations.status[selectedRequest.status]}
                                            sx={{
                                                backgroundColor: statusColors[selectedRequest.status],
                                                color: 'white'
                                            }}
                                        />
                                    </Box>
                                </Grid>
                                
                                <Grid item xs={12}>
                                    <Typography variant="h6">{selectedRequest.title}</Typography>
                                    {selectedRequest.description && (
                                        <Typography variant="body2" color="textSecondary" paragraph>
                                            {selectedRequest.description}
                                        </Typography>
                                    )}
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2">Categoria:</Typography>
                                    <Typography variant="body2">
                                        {categoryIcons[selectedRequest.category]} {translations.category[selectedRequest.category]}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2">Luogo:</Typography>
                                    <Typography variant="body2">
                                        {selectedRequest.location || 'N/D'}
                                        {selectedRequest.room_number && ` - Stanza ${selectedRequest.room_number}`}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2">Segnalato da:</Typography>
                                    <Typography variant="body2">{selectedRequest.reported_by || 'N/D'}</Typography>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2">Data segnalazione:</Typography>
                                    <Typography variant="body2">
                                        {new Date(selectedRequest.reported_date).toLocaleString('it-IT')}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2">Data prevista:</Typography>
                                    <Typography variant="body2">
                                        {selectedRequest.scheduled_date ? (
                                            <>
                                                {new Date(selectedRequest.scheduled_date).toLocaleDateString('it-IT')}
                                                {selectedRequest.scheduled_time && ` ore ${selectedRequest.scheduled_time.substring(0, 5)}`}
                                            </>
                                        ) : 'Da programmare'}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2">Manutentore:</Typography>
                                    <Typography variant="body2">
                                        {selectedRequest.company_name || 'Non assegnato'}
                                    </Typography>
                                </Grid>

                                {selectedRequest.estimated_cost && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Preventivo:</Typography>
                                        <Typography variant="body2">€{selectedRequest.estimated_cost}</Typography>
                                    </Grid>
                                )}

                                {selectedRequest.actual_cost && (
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Costo effettivo:</Typography>
                                        <Typography variant="body2">€{selectedRequest.actual_cost}</Typography>
                                    </Grid>
                                )}

                                {selectedRequest.is_recurring && (
                                    <Grid item xs={12}>
                                        <Chip
                                            label={`Ricorrente: ${translations.recurrencePattern[selectedRequest.recurrence_pattern]}`}
                                            variant="outlined"
                                            color="secondary"
                                        />
                                    </Grid>
                                )}

                                {selectedRequest.completion_notes && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2">Note di completamento:</Typography>
                                        <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                                            <Typography variant="body2">{selectedRequest.completion_notes}</Typography>
                                        </Paper>
                                    </Grid>
                                )}

                                {/* Storico Azioni */}
                                {selectedRequest.history && selectedRequest.history.length > 0 && (
                                    <Grid item xs={12}>
                                        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                                            Storico Interventi
                                        </Typography>
                                        <StandardTableContainer>
                                            <StandardTable size="small">
                                                <StandardTableHead>
                                                    <TableRow>
                                                        <StandardHeaderCell>Data</StandardHeaderCell>
                                                        <StandardHeaderCell>Azione</StandardHeaderCell>
                                                        <StandardHeaderCell>Eseguito da</StandardHeaderCell>
                                                        <StandardHeaderCell>Note</StandardHeaderCell>
                                                    </TableRow>
                                                </StandardTableHead>
                                                <TableBody>
                                                    {selectedRequest.history.map((h) => (
                                                        <TableRow key={h.history_id}>
                                                            <StandardBodyCell>
                                                                {new Date(h.action_date).toLocaleString('it-IT')}
                                                            </StandardBodyCell>
                                                            <StandardBodyCell>
                                                                <Chip label={h.action_type} size="small" />
                                                            </StandardBodyCell>
                                                            <StandardBodyCell>{h.performed_by}</StandardBodyCell>
                                                            <StandardBodyCell>{h.notes}</StandardBodyCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </StandardTable>
                                        </StandardTableContainer>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" color="primary" onClick={() => setOpenDetailDialog(false)}>{t('common.close', 'Chiudi')}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MaintenancePage;
